// src/components/common/KpiHero.tsx
//
// Headline KPI: a large, tight number (Soft minimal "financial" figure) with an
// optional delta chip and an inline SVG sparkline. Pure/presentational.
import React from 'react';
import { Box, Typography, Skeleton, alpha, useTheme } from '@mui/material';
import type { FormattedDelta } from '@/lib/analyticsFormat';
import { DeltaChip } from './DeltaChip';
import { WidgetCard } from './WidgetCard';

interface KpiHeroProps {
  label: string;
  value: string | number;
  sublabel?: string;
  /** A second, quieter line under the sublabel (e.g. the part of the total that another screen reports). */
  note?: string;
  delta?: FormattedDelta;
  sparkline?: number[];
  accent?: string;
  icon?: React.ReactNode;
  loading?: boolean;
  /** No data for the period: show `value` as a muted note instead of a big figure. */
  empty?: boolean;
}

const Sparkline: React.FC<{ points: number[]; color: string }> = ({ points, color }) => {
  const theme = useTheme();
  if (points.length < 2) return null;
  const w = 240;
  const h = 44;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const coords = points.map((p, i) => [i * step, h - ((p - min) / span) * h]);
  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  const gid = `spark-${color.replace('#', '')}`;
  return (
    <Box sx={{ mt: 'auto', pt: 1.5 }}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block', overflow: 'visible' }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity={0.22} />
            <stop offset="1" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gid})`} />
        <path d={line} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <Box sx={{ height: 0, borderBottom: `1px dashed ${alpha(theme.palette.divider, 0.8)}`, display: 'none' }} />
    </Box>
  );
};

export const KpiHero: React.FC<KpiHeroProps> = ({ label, value, sublabel, note, delta, sparkline, accent, icon, loading, empty }) => {
  const theme = useTheme();
  const tint = accent ?? theme.palette.dataAccent.dark;
  return (
    <WidgetCard title={label} icon={icon} accent={tint}>
      {loading ? (
        <Box sx={{ py: 1 }}>
          <Skeleton variant="text" width="55%" height={56} />
          <Skeleton variant="text" width="80%" />
        </Box>
      ) : empty ? (
        <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, color: 'text.secondary', lineHeight: 1.3, py: 1 }}>
          {value}
        </Typography>
      ) : (
        <>
          <Typography
            sx={{
              fontSize: { xs: 25, sm: 29 },
              fontWeight: 800,
              letterSpacing: '-0.6px',
              lineHeight: 1.05,
              // Uniform system: hero numbers are ink on paper; colour is
              // reserved for the icon tile / delta / sparkline accents.
              color: 'text.primary',
            }}
          >
            {value}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {sublabel && (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {sublabel}
              </Typography>
            )}
            {delta && <DeltaChip delta={delta} />}
          </Box>
          {note && (
            <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {note}
            </Typography>
          )}
          {sparkline && <Sparkline points={sparkline} color={tint} />}
        </>
      )}
    </WidgetCard>
  );
};
